import {
    IOption,
    CSSObject,
    Theme,
    SIDES,
    IActiveFilter,
    ICell,
    IFilterOption,
    ITableState,
    ITableToggle,
} from '../../base';
import { ITableForm } from '../../common';

export const getTableStickyWidths = (
    cells: Array<ICell>,
    rowSelection: boolean
): Array<number> => {
    const stickyWidths = cells.map((cell) => {
        if (cell.stickyColumn) {
            return cell.stickyColumn.width;
        }
        return 0;
    });
    rowSelection && stickyWidths.unshift(75);
    return stickyWidths;
};

const stickyDividerStyles = (theme: Theme, side: string): CSSObject => {
    return {
        content: "''",
        position: 'absolute',
        bottom: '0',
        left: side === SIDES.LEFT ? '100%' : undefined,
        right: side === SIDES.RIGHT ? '100%' : undefined,
        width: '2px',
        height: '100%',
        background: `${theme.palette.grey[500]}3D`,
        boxShadow: '0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
    };
};
export const stickyStyles = (
    theme: Theme,
    stickyPosition: string,
    columnIndex: number | undefined,
    columnWidth: number,
    stickyWidths?: Array<number>,
    isHeaderCell?: boolean,
    isSelected?: boolean
) => {
    const left =
        stickyPosition === SIDES.LEFT
            ? stickyWidths?.slice(0, columnIndex).reduce((a, b) => a + b, 0)
            : undefined;
    const right =
        stickyPosition === SIDES.RIGHT
            ? stickyWidths?.slice(0, columnIndex).reduce((a, b) => a + b, 0)
            : undefined;
    return {
        position: stickyPosition ? 'sticky' : undefined,
        left: left && left >= 0 ? `${left}px` : undefined,
        right: right && right >= 0 ? '0px' : undefined,
        zIndex: stickyPosition ? 1 : undefined,
        whiteSpace: 'nowrap',
        maxWidth: `${columnWidth}px`,
        minWidth: `${columnWidth}px`,
        background: stickyBackgroundColor(theme, isHeaderCell, isSelected),
        '&::after':
            left && left >= 0
                ? {
                      ...stickyDividerStyles(theme, SIDES.LEFT),
                  }
                : undefined,

        '&::before':
            right && right >= 0
                ? {
                      ...stickyDividerStyles(theme, SIDES.RIGHT),
                  }
                : undefined,
        '&:last-child': {
            paddingInlineEnd: theme.spacing(3),
            paddingInlineStart: theme.spacing(2),
        },
    };
};

export const stickyBackgroundColor = (
    theme: Theme,
    isHeaderCell?: boolean,
    isSelected?: boolean
) => {
    if (isHeaderCell) {
        return theme?.palette.grey[200];
    } else if (isSelected) {
        return '#F0F1F2'; // workaround for selected color missmatch due to opacity
    }
    return theme?.palette.grey[0];
};

export const getCellWidth = (stickyColumnValue: number, widthValue: number) => {
    if (stickyColumnValue) {
        return `${stickyColumnValue}px`;
    } else if (widthValue) {
        return `${widthValue}px`;
    }
    return 'auto';
};

export const formatTableFormFromState = (
    tableState: ITableState,
    filterStructure: Array<IFilterOption>,
    toggleConfig?: Array<ITableToggle>
): ITableForm => {
    const filters: Array<IActiveFilter> = [];

    Object.entries(tableState.filters).forEach(([filterId, values]) => {
        const filterOption = filterStructure.find(
            (option) => option.id === filterId
        );

        if (filterOption) {
            filters.push({
                filterId,
                values: values.map((v) => ({
                    value: v,
                    label:
                        filterOption?.optionValues?.find(
                            (value) => value.key === String(v)
                        )?.label || v,
                    key: v,
                })) as Array<IOption>,
                type: filterOption.type,
            });
        }
    });

    const toggles: Record<string, boolean> = {};
    if (toggleConfig) {
        toggleConfig.forEach((toggle) => {
            const urlParamValues = tableState.filters[toggle.key];
            if (urlParamValues !== undefined) {
                toggles[toggle.key] = urlParamValues[0] === 'true';
            } else {
                toggles[toggle.key] = toggle.defaultValue;
            }
        });
    }

    return {
        filters,
        search: tableState.search,
        sort: tableState.sort,
        tableId: tableState.id,
        selectedRows: tableState.selectedRows,
        toggles,
    };
};

export const formatDateAndTime = (dateString: string) => {
    const date = dateString ? new Date(dateString) : null;

    // Format the date as DD/MM/YYYY
    const formattedDate = date?.toLocaleDateString('en-GB'); // 'en-GB' gives DD/MM/YYYY format

    // Extract time if it exists
    const timeMatch = dateString?.match(/T(\d{2}:\d{2}:\d{2})/);
    const formattedTime = timeMatch ? timeMatch[1] : null;

    return {
        date: formattedDate,
        time: formattedTime,
    };
};
